import React, { useState, useCallback } from "react";
import { useStartMessages } from "../../hooks/use-start-messages";
import { useGlobalClient } from "../../hooks/use-global-client";
import { StartMessageFormModal } from "../../components/start-messages/start-message-form-modal";
import { StartMessageSearchFilters } from "../../components/start-messages/start-message-search-filters";
import { StartMessagePreview } from "../../components/start-messages/start-message-preview";
import { StartMessageAnalytics } from "../../components/start-messages/start-message-analytics";
import { RealTimeUsageStats } from "../../components/start-messages/real-time-usage-stats";
import { Dialog } from "../../components/ui/dialog";
import type {
  StartMessage,
  StartMessageFilterInput,
  CreateStartMessageInput,
  UpdateStartMessageInput,
} from "../../types/start-message-types";

export function StartMessagesPage(): React.ReactElement {
  // Get global client selection (handles both super admin and regular users)
  const { selectedClient, isSuperAdmin } = useGlobalClient();

  // State
  const [filter, setFilter] = useState<StartMessageFilterInput>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<StartMessage | null>(
    null
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<StartMessage | null>(
    null
  );
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Hook
  const {
    startMessages,
    loading,
    error,
    total,
    hasMore,
    stats,
    refetch,
    loadMore,
    clearFilter,
    createStartMessage,
    updateStartMessage,
    deleteStartMessage,
  } = useStartMessages({ filter, limit: 20 });

  // Handlers
  const handleFilterChange = useCallback(
    (newFilter: StartMessageFilterInput) => {
      setFilter(newFilter);
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilter({});
    clearFilter();
  }, [clearFilter]);

  const handleCreateMessage = useCallback(() => {
    setSelectedMessage(null);
    setShowCreateModal(true);
  }, []);

  const handleEditMessage = useCallback((message: StartMessage) => {
    setSelectedMessage(message);
    setShowCreateModal(true);
  }, []);

  const handleViewMessage = useCallback((message: StartMessage) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
  }, []);

  const handleDeleteMessage = useCallback((message: StartMessage) => {
    setMessageToDelete(message);
    setShowDeleteConfirmModal(true);
  }, []);

  const handleFormSubmit = useCallback(
    async (data: CreateStartMessageInput | UpdateStartMessageInput) => {
      try {
        if (selectedMessage) {
          await updateStartMessage(
            selectedMessage.id,
            data as UpdateStartMessageInput
          );
        } else {
          await createStartMessage(data as CreateStartMessageInput);
        }
        setShowCreateModal(false);
        setSelectedMessage(null);
        refetch();
      } catch (error) {
        console.error("Failed to save start message:", error);
        throw error;
      }
    },
    [selectedMessage, createStartMessage, updateStartMessage, refetch]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!messageToDelete) return;

    try {
      await deleteStartMessage(messageToDelete.id);
      setShowDeleteConfirmModal(false);
      setMessageToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete start message:", error);
    }
  }, [messageToDelete, deleteStartMessage, refetch]);

  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
    setSelectedMessage(null);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setShowDetailModal(false);
    setSelectedMessage(null);
  }, []);

  // Get status badge color
  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive
      ? "bg-green-100 text-green-800"
      : "bg-gray-100 text-gray-800";
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Show client selection prompt for super admins who haven't selected a client
  if (isSuperAdmin && !selectedClient) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Start Messages</h1>
          <p className="mt-2 text-gray-600">
            Create and manage reusable start messages that are prefilled in SMS
            apps or automatically sent as the first message in chat interfaces.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Select a Client
          </h3>
          <p className="text-gray-600 mb-4">
            Please select a client from the dropdown in the header to view and
            manage start messages.
          </p>
          <p className="text-sm text-gray-500">
            As a super admin, you can switch between different clients using the
            client selector in the top navigation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Start Messages</h1>
        <p className="mt-2 text-gray-600">
          Create and manage reusable start messages that are prefilled in SMS
          apps or automatically sent as the first message in chat interfaces.
        </p>
        {isSuperAdmin && selectedClient && (
          <p className="mt-1 text-sm text-blue-600">
            Managing start messages for:{" "}
            <span className="font-medium">{selectedClient.clientName}</span>
          </p>
        )}
      </div>

      {/* Analytics Toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            showAnalytics
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {showAnalytics ? "Hide Analytics" : "Show Analytics"}
        </button>
      </div>

      {/* Analytics Section */}
      {showAnalytics && <StartMessageAnalytics showTenantSummary={true} />}

      {/* Quick Stats */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Messages
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.total}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Messages
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.active}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Usage</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.totalUsage}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Length</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : `${stats.averageLength} chars`}
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Create New Message Section */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Create New Start Message
            </h2>
            <button
              onClick={handleCreateMessage}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              data-testid="create-start-message-button"
            >
              Create Start Message
            </button>
          </div>
        </div>
        <div className="p-6">
          <div
            className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer"
            onClick={handleCreateMessage}
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <p className="text-gray-500">
              Start messages are automatically prefilled in SMS apps or sent as
              the first chat message
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <StartMessageSearchFilters
        filter={filter}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Messages List */}
      <div
        className="bg-white rounded-lg shadow border"
        data-testid="start-messages-page"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Start Messages
            </h2>
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium disabled:opacity-50"
              >
                Load More
              </button>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {/* Loading State */}
          {loading && startMessages.length === 0 && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading start messages...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading start messages
              </h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && startMessages.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No start messages found
              </h3>
              <p className="text-gray-500 mb-4">
                Create your first start message to get started.
              </p>
              <button
                onClick={handleCreateMessage}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create Start Message
              </button>
            </div>
          )}

          {/* Messages List */}
          {!loading && !error && startMessages.length > 0 && (
            <>
              {startMessages.map((message) => (
                <div
                  key={message.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {message.name}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(
                            message.isActive
                          )}`}
                        >
                          {message.isActive ? "Active" : "Inactive"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {message.content.length}/140 chars
                        </span>
                      </div>

                      <p className="text-gray-600 mb-3 font-mono text-sm bg-gray-50 p-2 rounded">
                        "{message.content}"
                      </p>

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Used {message.usageCount} times</span>
                        <span>•</span>
                        <span>Created {formatDate(message.createdAt)}</span>
                        {message.conversationIds.length > 0 && (
                          <>
                            <span>•</span>
                            <span>
                              {message.conversationIds.length} conversations
                            </span>
                          </>
                        )}
                        {message.tags && message.tags.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <span>•</span>
                            <span>Tags:</span>
                            {message.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewMessage(message)}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="View details"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleEditMessage(message)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        title="Edit message"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteMessage(message)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete message"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Form Modal */}
      <StartMessageFormModal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        onSubmit={handleFormSubmit}
        startMessage={selectedMessage}
      />

      {/* Detail Modal */}
      {selectedMessage && (
        <Dialog
          isOpen={showDetailModal}
          onClose={handleDetailModalClose}
          title={selectedMessage.name}
          size="xl"
        >
          <div className="space-y-6">
            {/* Message Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Status:</span>
                  <span
                    className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(
                      selectedMessage.isActive
                    )}`}
                  >
                    {selectedMessage.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Usage Count:
                  </span>
                  <span className="ml-2 text-gray-900">
                    {selectedMessage.usageCount}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Character Count:
                  </span>
                  <span className="ml-2 text-gray-900">
                    {selectedMessage.content.length}/140
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Conversations:
                  </span>
                  <span className="ml-2 text-gray-900">
                    {selectedMessage.conversationIds.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Preview */}
            <StartMessagePreview content={selectedMessage.content} />

            {/* Real-time Usage Statistics */}
            <RealTimeUsageStats
              messageId={selectedMessage.id}
              enableSubscriptions={true}
              showRecentActivity={true}
              maxRecentActivity={5}
            />

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleDetailModalClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDetailModalClose();
                  handleEditMessage(selectedMessage);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
              >
                Edit Message
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {messageToDelete && (
        <Dialog
          isOpen={showDeleteConfirmModal}
          onClose={() => setShowDeleteConfirmModal(false)}
          title="Delete Start Message"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete "{messageToDelete.name}"? This
              action cannot be undone.
            </p>

            {messageToDelete.conversationIds.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-yellow-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <span className="text-sm text-yellow-800">
                    This message is attached to{" "}
                    {messageToDelete.conversationIds.length} conversation(s).
                    Deleting it will remove it from all conversations.
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Delete Message
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
